import { displayName } from '@/lib/jobTitles';

/**
 * Every report on the Reports page is grouped under one heading per Property
 * Manager - on screen, in the CSV and in the PDF - so a PM can find their own
 * projects together and the Construction Manager can walk the list person by
 * person.
 *
 * `project.property_manager` holds an email address, because the project form
 * picks a person from the user directory. The heading is that person's name,
 * and the groups are in name order, not email order: "ereiss@" and "Evan
 * Reiss" do not sort to the same place. A PM who is no longer in the
 * directory keeps their email as the heading rather than being folded into
 * "No Property Manager", which would hide that the project names somebody.
 *
 * Within a group the projects keep the order they were handed in, so each
 * report sorts first (newest update, project name, a clicked column) and then
 * groups - the grouping never undoes a report's own order.
 */

export const NO_PM_HEADING = 'No Property Manager';

/** The raw assignment, trimmed; '' when the project names nobody. */
function pmKey(project) {
  return (project.property_manager || '').trim().toLowerCase();
}

/**
 * The projects, each with `property_manager_label` - the PM's name from the
 * user directory, or the stored value when the directory does not know it.
 * Copies, so the query cache's objects are not changed underneath it.
 */
export function withPmNames(projects, users = []) {
  const byEmail = new Map();
  for (const u of users) {
    if (u?.email) byEmail.set(u.email.trim().toLowerCase(), displayName(u));
  }
  return projects.map(p => {
    const raw = (p.property_manager || '').trim();
    return { ...p, property_manager_label: raw ? (byEmail.get(raw.toLowerCase()) || raw) : '' };
  });
}

/** What a report prints for a project's PM. Works before or without withPmNames. */
export function pmLabel(project) {
  return project.property_manager_label ?? (project.property_manager || '').trim();
}

/**
 * `[{ key, heading, projects }]`, one entry per Property Manager, A to Z by
 * name, with projects that name nobody last under NO_PM_HEADING.
 */
export function groupByPropertyManager(projects) {
  const groups = new Map();
  for (const p of projects) {
    const key = pmKey(p);
    if (!groups.has(key)) groups.set(key, { key, heading: pmLabel(p) || NO_PM_HEADING, projects: [] });
    groups.get(key).projects.push(p);
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.key !== !b.key) return a.key ? -1 : 1;
    return a.heading.localeCompare(b.heading, undefined, { sensitivity: 'base' })
      || a.key.localeCompare(b.key);
  });
}

/** The same order as the headings, flattened - for a CSV, which has no headings. */
export function sortByPropertyManager(projects) {
  return groupByPropertyManager(projects).flatMap(g => g.projects);
}

/** How tall drawPdfPmHeading is, in the document's units, so a caller can keep it with its first project. */
export const PDF_PM_HEADING_H = 9;

/**
 * A Property Manager heading bar in a jsPDF report, its top at `y`. A group
 * that runs onto a new page gets the heading again, marked "(continued)", so
 * a page read on its own still says whose projects it holds.
 */
export function drawPdfPmHeading(doc, { x, y, width, group, continued = false }) {
  const n = group.projects.length;
  doc.setFillColor(226, 232, 240);
  doc.rect(x, y, width, PDF_PM_HEADING_H - 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(`${group.heading}${continued ? ' (continued)' : ''}`, x + 2, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);
  doc.text(`${n} project${n === 1 ? '' : 's'}`, x + width - 2, y + 5, { align: 'right' });
}

/** A to Z by project name, the order used inside a group unless a report has its own. */
export function byProjectName(a, b) {
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
}

/**
 * Most recently updated first; projects with no update after them, by name.
 * `timeOf` is latestUpdateTime, which is -Infinity for no update - and
 * -Infinity minus -Infinity is NaN, which a sort comparator must never return.
 */
export function byNewestUpdate(timeOf) {
  return (a, b) => {
    const ta = timeOf(a);
    const tb = timeOf(b);
    if (ta === tb) return byProjectName(a, b);
    if (ta === -Infinity) return 1;
    if (tb === -Infinity) return -1;
    return tb - ta;
  };
}
